import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { BRICK_SPECS, formatMm } from '@bw/core';
import type { BrickId, PlacedBrick } from '@bw/core';
import { Modal } from './Dialogs.js';
import { useEditor } from '../state/store.js';
import { plural } from '../copy.js';

/**
 * Design document C8 — the structured alternative view.
 *
 * A canvas says nothing to a screen reader, so the same wall is also a
 * navigable list of courses and bricks. Useful to sighted users too, for
 * checking setting-out.
 */
export function WallOutline({
  onClose,
  onFocusBrick,
}: {
  readonly onClose: () => void;
  readonly onFocusBrick: (id: BrickId) => void;
}): JSX.Element {
  const project = useEditor((s) => s.project);
  const selected = useEditor((s) => s.selected);
  const docRevision = useEditor((s) => s.docRevision);
  const model = project.activeWall.model;

  const courses = useMemo(() => {
    const byBaseline = new Map<number, PlacedBrick[]>();
    for (const brick of model.bricks) {
      const bucket = byBaseline.get(brick.y);
      if (bucket === undefined) byBaseline.set(brick.y, [brick]);
      else bucket.push(brick);
    }
    return [...byBaseline.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([baseline, bricks], index) => ({
        number: index + 1,
        baseline,
        bricks: [...bricks].sort((a, b) => a.x - b.x),
      }));
  }, [model, docRevision]);

  // Rendering every brick up front put 2,475 buttons in the DOM the moment the
  // dialog opened — on the accessibility view, of all places (F13).
  const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set());
  const toggle = (baseline: number): void =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(baseline)) next.delete(baseline);
      else next.add(baseline);
      return next;
    });

  return (
    <Modal title="Wall outline" onClose={onClose}>
      <p className="note">
        {project.activeWall.name} · {plural(model.size)} in {courses.length} course
        {courses.length === 1 ? '' : 's'}. Choose a brick to select it and bring it into view.
      </p>
      <div className="outline">
        {courses.length === 0 && <p className="note">This wall is empty.</p>}
        {courses.map((course) => (
          <details
            className="course"
            key={course.baseline}
            open={open.has(course.baseline)}
            onToggle={(event) => {
              const isOpen = (event.currentTarget as HTMLDetailsElement).open;
              if (isOpen !== open.has(course.baseline)) toggle(course.baseline);
            }}
          >
            <summary>
              Course {course.number}
              <span className="meta">
                Y {formatMm(course.baseline)} mm · {plural(course.bricks.length)}
              </span>
            </summary>
            <ul>
              {open.has(course.baseline) &&
                course.bricks.map((brick) => (
                <li key={brick.id}>
                  <button
                    onClick={() => {
                      onFocusBrick(brick.id);
                      onClose();
                    }}
                  >
                    {BRICK_SPECS[brick.kind].name} · {brick.rotation}° · X {formatMm(brick.x)} to{' '}
                    {formatMm(brick.right)} mm
                    {selected.has(brick.id) ? ' · selected' : ''}
                  </button>
                </li>
                ))}
            </ul>
          </details>
        ))}
      </div>
    </Modal>
  );
}
